import * as React from "react";

import clsxMerge from "@v1/components/designSystem/shadcn/utils/cn-merge";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

const ICON_DIMENSIONS = "h-4 w-4";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={clsxMerge("", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={clsxMerge(
      "flex flex-row items-center justify-end gap-1",
      className,
    )}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={clsxMerge("text-placeholder", className)}
    {...props}
  />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
  isNavigationButton: boolean;
  size?: string;
  isDisabled?: boolean;
} & React.ComponentProps<"a">;

const PaginationLink = ({
  className,
  isActive,
  isNavigationButton,
  isDisabled = false,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={clsxMerge(
      "inline-flex items-center justify-center rounded-md text-sm font-medium",
      isActive &&
        !isNavigationButton &&
        "text-primary border-0.5 border-input border-primary",
      !isNavigationButton && "h-10 w-10",
      !isActive && !isNavigationButton && "hover:bg-sky-soft",
      !isDisabled && "hover:text-primary",
      isDisabled && "cursor-not-allowed",
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationNavigationButton = ({
  direction,
  className,
  ...props
}: {
  direction: "previous" | "next";
} & React.ComponentProps<typeof PaginationLink>) => {
  const isPrevious = direction === "previous";

  const label = isPrevious ? "Previous" : "Next";

  const Icon = isPrevious ? ChevronLeft : ChevronRight;

  return (
    <PaginationLink
      aria-label={`Go to ${label.toLowerCase()} page`}
      className={clsxMerge(`flex items-center justify-center`, className)}
      {...props}
    >
      <Icon className={ICON_DIMENSIONS} />
    </PaginationLink>
  );
};

PaginationNavigationButton.displayName = "PaginationNavigationButton";

const PaginationPrevious = (
  props: React.ComponentProps<typeof PaginationLink>,
) => <PaginationNavigationButton direction="previous" {...props} />;
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = (props: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationNavigationButton direction="next" {...props} />
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={clsxMerge("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className={ICON_DIMENSIONS} />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
